import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { UserTypeEnum } from 'src/modules/authentication/enums/user-type.enum';
import { ConfigService } from 'src/app/services/config.service';

// Hosts where the contractor experience is not supported in a browser.
// Native builds run from localhost / file:// so they never match, and neither
// do local dev servers or staging hosts - those stay usable for testing.
const APP_ONLY_HOSTS = ['app.staffy.com'];

const APP_STORE_URL = 'https://apps.apple.com/app/id1133559351';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ca.staffy.app';

@Injectable({
  providedIn: 'root'
})
export class UnsupportedWebAccessService {
  private sendToAppRequiredScreenAfterLogout = false;

  constructor(
    private platform: Platform,
    private configService: ConfigService
  ) {}

  // A contractor reaching the dashboard through the web app has to be sent to the
  // download screen instead. Employers are unaffected - the web app is built for them.
  // Demo mode is exempt too, so demo contractor accounts can still be shown in a browser.
  // Contractors still onboarding can finish signup here; the block starts once it's complete.
  isContractorOnUnsupportedWeb(user: IUser): boolean {
    if (!user || user.type === UserTypeEnum.EMPLOYER || this.configService.isDemoMode()) {
      return false;
    }
    if (!user.isSignupProcessComplete) {
      return false;
    }
    return this.isUnsupportedWebHost();
  }

  isUnsupportedWebHost(): boolean {
    if (this.platform.is('cordova') || this.platform.is('capacitor')) {
      return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return APP_ONLY_HOSTS.indexOf(host) > -1;
  }

  flagAppRequiredScreen() {
    this.sendToAppRequiredScreenAfterLogout = true;
  }

  // Read once by the logout effect, right after the session is cleared, so a forced
  // logout lands on the download screen while every other logout lands on login.
  consumePostLogoutRoute(): string {
    if (!this.sendToAppRequiredScreenAfterLogout) {
      return 'auth/login';
    }
    this.sendToAppRequiredScreenAfterLogout = false;
    return 'auth/app-required';
  }

  get appStoreUrl(): string {
    return APP_STORE_URL;
  }

  get playStoreUrl(): string {
    return PLAY_STORE_URL;
  }

  // Which badge to lead with on the download screen
  isIosDevice(): boolean {
    return this.platform.is('ios') || this.platform.is('ipad') || this.platform.is('iphone');
  }
}
